import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

interface TechSlide {
  title: string;
  image: string;
}

@Component({
  selector: 'app-suite-technologies',
  imports: [CommonModule],
  templateUrl: './suite-technologies.component.html',
  styleUrls: ['./suite-technologies.component.scss'],
  standalone: true
})
export class SuiteTechnologiesComponent implements OnInit, OnDestroy, AfterViewInit {
  currentSlide = 0;
  private autoSlideInterval: any;

  @ViewChildren('videoPlayer') videoElements!: QueryList<ElementRef<HTMLVideoElement>>;

  slides: TechSlide[] = [
    { title: 'FRONT END', image: '../../../assets/home/technologies/slider-images/frontend.webm' },
    { title: 'BACK END', image: '../../../assets/home/technologies/slider-images/backend.webm' },
    { title: 'AI & ML', image: '../../../assets/home/technologies/slider-images/artificialintelligence.webm' },
    { title: 'DATABASE', image: '../../../assets/home/technologies/slider-images/database.webm' },
    { title: 'MOBILE APPS', image: '../../../assets/home/technologies/slider-images/mobileapp.webm' },
    { title: 'DEVOPS', image: '../../../assets/home/technologies/slider-images/devops.webm' },
    { title: 'QA', image: '../../../assets/home/technologies/slider-images/qa.webm' },
    { title: 'CLOUD', image: '../../../assets/home/technologies/slider-images/cloud.webm' }
  ];

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  ngOnInit(): void {
    // We start auto slide after view init to ensure videos are ready
  }

  ngAfterViewInit(): void {
    // Video playback and the auto-slide interval are browser-only. Running them
    // during SSR/prerender schedules a recurring timer that prevents the app from
    // ever stabilizing, which makes the prerender of every page using this
    // component time out.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    // Slight delay to ensure DOM is ready before playing first video
    setTimeout(() => {
      this.updateVideoPlayback();
      this.startAutoSlide();
    }, 100);
  }

  ngOnDestroy(): void {
    this.stopAutoSlide();
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Clear any existing interval
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, 5000);
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    this.updateVideoPlayback();
  }

  goToSlide(index: number): void {
    this.currentSlide = index;
    this.updateVideoPlayback();
    // Restart timer when user interacts
    this.startAutoSlide();
  }

  private updateVideoPlayback(): void {
    if (!this.videoElements) return;

    const videos = this.videoElements.toArray();

    videos.forEach((videoRef, index) => {
      const video = videoRef.nativeElement;

      if (index === this.currentSlide) {
        // 1. Force reset time to start
        video.currentTime = 0;

        // 2. Since we set preload="none", we must manually load if not ready
        // readyState 0 means HAVE_NOTHING
        if (video.readyState === 0) {
          if (typeof video.load === 'function') {
            video.load();
          }
        }

        // 3. Play
        const playPromise = (typeof video.play === 'function' ? video.play() : Promise.resolve());
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Auto-play was prevented:', error);
          });
        }
      } else {
        // Pause others and reset their time so they are ready next time
        if (typeof video.pause === 'function') {
          video.pause();
        }
        video.currentTime = 0;
      }
    });
  }
}